/**
 * SB6-II-01 — inline Assigned Users cell.
 *
 * Renders the row's assignee chips (`{first} {last} ({tenant})`); `"-"`
 * when none. Clicking opens an MUI `<Popover>` with a multi-select
 * `<Autocomplete>` over `assignable-users` (lazy-loaded on open,
 * typeahead-filtered). Commit fires `PATCH {assignedUserIds}` with an
 * optimistic update across the cached list pages + rollback on error +
 * a toast — mirroring the Data Acquisitions `AssigneeChip` ergonomics.
 *
 * Assigning the first user to a CREATED row flips it to SCHEDULED on the
 * BE; the optimistic write keeps the current state and the
 * list-invalidation in `usePatchInspectionInstructionMutation` reconciles
 * the new state when the PATCH response lands (AC9).
 */
import { useMemo, useState, type ReactElement } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Popover,
  Stack,
  TextField,
  Typography,
  type TextFieldProps,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "../account/ToastContext.js";
import { iiKeys } from "./keys.js";
import { useIiAssignableUsersQuery } from "./useInspectionInstructionFacets.js";
import { usePatchInspectionInstructionMutation } from "./mutations.js";
import { applyOptimisticRowPatch } from "./optimisticListUpdate.js";
import type { AssignedUser, IiListItem, IiListResponse } from "./types.js";
import { userLabel } from "./userLabel.js";

export interface AssignedUsersCellProps {
  row: IiListItem;
}

export function AssignedUsersCell({
  row,
}: AssignedUsersCellProps): ReactElement {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState("");
  const open = anchorEl !== null;

  const assignable = useIiAssignableUsersQuery(open);
  const patch = usePatchInspectionInstructionMutation();

  const current = row.assignedUsers;

  // Union of assignable roster + the row's current assignees so the
  // already-picked values always resolve to a labelled option.
  const options = useMemo<AssignedUser[]>(() => {
    const seen = new Set<string>();
    const out: AssignedUser[] = [];
    for (const u of current) {
      if (!seen.has(u.userId)) {
        seen.add(u.userId);
        out.push(u);
      }
    }
    for (const u of assignable.data?.users ?? []) {
      if (!seen.has(u.userId)) {
        seen.add(u.userId);
        out.push(u);
      }
    }
    return out;
  }, [current, assignable.data?.users]);

  function handleOpen(e: React.MouseEvent<HTMLElement>): void {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  }
  function handleClose(): void {
    setAnchorEl(null);
    setSearch("");
  }

  function commit(next: AssignedUser[]): void {
    const userIds = next.map((u) => u.userId);
    // Optimistic update across every cached list page: swap the row's
    // assignedUsers, then reconcile on settle (list invalidation).
    const family = iiKeys.lists();
    const snapshots = qc.getQueriesData<IiListResponse>({ queryKey: family });
    for (const [key, data] of applyOptimisticRowPatch(
      snapshots,
      row.inspectionInstructionId,
      { assignedUsers: next },
    )) {
      if (data) qc.setQueryData(key, data);
    }
    patch.mutate(
      { id: row.inspectionInstructionId, body: { assignedUserIds: userIds } },
      {
        onError: () => {
          // Rollback every snapshot we touched.
          for (const [key, data] of snapshots) {
            if (data) qc.setQueryData(key, data);
          }
          toast("Couldn't update assignees — try again.", "error");
        },
        onSuccess: () => {
          toast("Assignees updated.");
        },
      },
    );
  }

  return (
    <>
      <Box
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        aria-haspopup="dialog"
        aria-label={`Edit assigned users (${
          current.length === 0 ? "none" : current.map(userLabel).join(", ")
        })`}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setAnchorEl(e.currentTarget);
          }
        }}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          alignItems: "center",
          width: "100%",
          cursor: "pointer",
          minHeight: 24,
        }}
      >
        {current.length === 0 ? (
          <Typography variant="body2" component="span" color="text.secondary">
            -
          </Typography>
        ) : (
          current.map((u) => (
            <Chip key={u.userId} size="small" label={userLabel(u)} />
          ))
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: { width: 360, p: 1.5 } } }}
      >
        <Stack spacing={1}>
          <Typography variant="caption" color="text.secondary">
            Assign users
          </Typography>
          <Autocomplete<AssignedUser, true>
            multiple
            disableCloseOnSelect
            openOnFocus
            size="small"
            options={options}
            value={current}
            loading={assignable.isPending}
            inputValue={search}
            onInputChange={(_e, value, reason) => {
              if (reason === "reset") return;
              setSearch(value);
            }}
            getOptionLabel={userLabel}
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            onChange={(_e, next) => {
              commit(next);
            }}
            renderTags={(picked, getTagProps) =>
              picked.map((o, index) => {
                const { key, ...rest } = getTagProps({ index });
                return (
                  <Chip {...rest} key={key} size="small" label={userLabel(o)} />
                );
              })
            }
            renderInput={(p) => {
              const { InputLabelProps, InputProps, inputProps, ...rest } = p;
              const tfProps = {
                ...rest,
                placeholder: "Search users…",
                slotProps: {
                  htmlInput: { ...inputProps, "aria-label": "Search users" },
                  input: {
                    ...InputProps,
                    endAdornment: (
                      <>
                        {assignable.isPending ? (
                          <CircularProgress size={14} />
                        ) : null}
                        {InputProps.endAdornment}
                      </>
                    ),
                  },
                  inputLabel: InputLabelProps,
                },
              } as unknown as TextFieldProps;
              return <TextField {...tfProps} />;
            }}
          />
        </Stack>
      </Popover>
    </>
  );
}
